import { BindAll } from 'lodash-decorators'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiHomework, EnumApiShow, EnumApiUser } from '@xt/client/api'
import { mapCollectType } from '@xt/client/utils/moment/map'
import { action, observable, computed } from 'mobx'
import type { HomeworkDetail, DairyDetil } from './types.d'
import { EnumContentType, EnumVisibleRange, MomentSourceType } from '@xt/client/enums'
import { UserComments } from './comments'
import { EnumFollowStatus } from '@xt/client/enums/my'

export type HomeworkOption = {
  // 内容主体为作业
  type: 'homework'
  // 作业id 必填
  homeworkId: number
}

export type DairyOption = {
  // 内容主体为日记
  type: 'dairy'
  // 日记id 必填
  momentId: string
}

// 组件必填参数
export type Options = HomeworkOption | DairyOption

type PrivateOptions =
  | (HomeworkOption & {
      momentId?: string
    })
  | (DairyOption & {
      homeworkId?: number
    })

/**
 * 前端的数据源
 *
 * 由于内容主体可能是日记 也可能是作业 这里统一字段方便页面渲染
 */
export type Content = {
  /**
   * 内容主体id
   * 如果是作业则是作业id number
   * 如果是日记则是日记id string
   */
  contentId: number | string
  imageList: Array<{
    originalUrl: string
    originalWidth: number
    originalHeight: number
    waterUrl: string
  }>
  // 内容作者memberId
  memberId: string
  // 内容作者nickname
  memberNickname: string
  // 标题
  title: string
  // 富文本
  content: string
  // 如果是作业 存在这个字段 表示作业是否被批阅过
  reviewed?: boolean
  // 标签
  tag: string
  // 内容创建时间
  createTime: number
  // 当前内容是否公开
  isPublic: boolean
  // 内容是否违规
  isViolation: number
  // 是否允许点赞收藏
  allowLikeOrCollect: boolean
  // 是否允许分享
  allowShare: boolean
  // 是否本人喜欢
  isLike: boolean
  // 是否本人收藏
  isCollect: boolean
  // 喜欢总数
  likeCount: number
  // 收藏总数
  collectCount: number
  // 收藏接口参数
  collectType: null | number
  // 当前内容是否是视频内容
  videoUrl: string
  // 视频封面图
  videoCover: string
  // 等待评阅标识
  showReviewedflag: boolean
  // 允许批阅操作
  allowReviewed: boolean
}

export enum EnumConentError {
  /**
   * 用户访问的内容已经被作者删除
   */
  Removed,
  /**
   * 用户访问的内容已经被作者设为私密
   */
  Private
}

@BindAll()
export class UserContent {
  constructor() {}

  @observable loading = true
  @observable content: null | Content = null
  // 当前内容的分享二维码
  @observable shareImage: string | null = null
  @observable isLoadingShareImage: boolean = true

  @observable contentError: null | EnumConentError = null
  @observable isFollow: boolean = false

  // 扩展options
  fullOptions: PrivateOptions | null = null

  @computed
  get isVideo() {
    return this.content?.videoUrl ? true : false
  }

  // 作业id和日记id的映射缓存
  private momentIdCache = new Map<number, null | string>()
  private worksMomentIdCache = new Map<number, null | string>()

  // 用户评论数据
  UserComments = new UserComments()

  @action
  private setIsFollow(isFollow: boolean) {
    this.isFollow = isFollow
  }

  @action
  private updateContent(content: Content | null) {
    this.content = content
  }

  @action
  private updateLoading(isLoading: boolean) {
    this.loading = isLoading
  }

  @action updateContentError(error: EnumConentError) {
    this.contentError = error
  }

  @action
  public reset() {
    this.loading = true
    this.content = null
    this.shareImage = null
    this.isLoadingShareImage = true
    this.fullOptions = null
    this.contentError = null
    this.isFollow = false
    this.UserComments.reset()
  }

  /**
   * 切换内容可见性 公开私密功能：存在作业id，一律掉作业接口；不存在作业id，掉日记接口
   * @param data
   * @returns
   */
  public async toggleVisibleRange() {
    if (!this.content || !this.fullOptions) return false

    const nextVisibleRange: EnumVisibleRange = this.content.isPublic ? EnumVisibleRange.Privacy : EnumVisibleRange.Public

    if (this.fullOptions.homeworkId) {
      const [, success] = await $service<boolean>({
        url: nextVisibleRange === EnumVisibleRange.Public ? EnumApiHomework.homeworkPublish : EnumApiHomework.homeworkPrivate,
        method: 'PUT',
        data: { id: this.fullOptions.homeworkId }
      })

      if (success === true) {
        this.updateContentVisibleRange(nextVisibleRange === EnumVisibleRange.Public)
        return true
      }
    } else if (this.fullOptions.momentId) {
      const [, success] = await $service<boolean>({
        url: EnumApiHomework.MomentVisible,
        method: 'PUT',
        data: { id: this.fullOptions.momentId, visibleRange: nextVisibleRange }
      })

      if (success === true) {
        this.updateContentVisibleRange(nextVisibleRange === EnumVisibleRange.Public)
        return true
      }
    }

    return false
  }

  // 删除一个内容 删除功能：存在作业id，一律掉作业接口；不存在作业id，掉日记接口
  public async removeContent() {
    if (!this.fullOptions) return false

    if (this.fullOptions.homeworkId) {
      const [, success] = await $service<boolean>({
        url: EnumApiHomework.HomeworkDelete,
        method: 'DELETE',
        data: { id: this.fullOptions.homeworkId }
      })

      return success === true
    } else if (this.fullOptions.momentId) {
      const [, success] = await $service<boolean>({
        url: EnumApiHomework.MomentDelete,
        method: 'DELETE',
        data: { id: this.fullOptions.momentId }
      })

      return success === true
    }
  }

  // 加载当前内容的分享码
  public async loadContentShareImage() {
    if (!this.fullOptions?.momentId) return

    if (!this.content) return

    this.updateLoadingShareImage(true)

    const page = this.content.videoUrl ? 'pages/moments/videos/index' : 'pages/homework/content/index'

    const [error, data] = await $service<Blob>({
      url: EnumApiShow.appletsQrcode,
      data: {
        page,
        query: `${this.fullOptions.momentId}`,
        appletsType: 2,
        hyaline: true
      },
      params: {
        page,
        query: `${this.fullOptions.momentId}`,
        appletsType: 2,
        hyaline: true
      },
      responseType: 'blob',
      headers: {
        'Content-Type': 'image/jpeg'
      }
    })

    this.updateLoadingShareImage(false)

    if (error) return

    const url = window.URL.createObjectURL(data)

    this.updateContentShareImage(url)
  }

  @action
  public revokeShareImage() {
    if (!this.shareImage) return

    window.URL.revokeObjectURL(this.shareImage)
  }

  // 点赞一个日记
  public async likeMoment() {
    if (!this.fullOptions?.momentId) return

    this.toggleContentLike(true)

    const [, success] = await $service<boolean>({
      url: EnumApiHomework.MomentLike,
      method: 'PUT',
      data: { momentId: this.fullOptions.momentId }
    })

    if (!success) {
      this.toggleContentLike(false)
    }

    return success === true
  }

  // 收藏一个日记
  public async collectMoment(): Promise<{ isSuccess: boolean; type: 'collect' | 'uncollect' }> {
    if (!this.fullOptions?.momentId) return { isSuccess: false, type: 'collect' }

    if (!this.content) return { isSuccess: false, type: 'collect' }

    if (this.content.collectType === null) return { isSuccess: false, type: 'collect' }

    const nextState = !this.content.isCollect

    this.toggleContentCollect(nextState)

    const [, success] = await $service<boolean>({
      url: EnumApiHomework.MomentCollect,
      method: 'PUT',
      data: {
        workId: this.fullOptions.momentId,
        collect: nextState ? 'true' : 'false',
        collectType: this.content.collectType
      }
    })

    if (!success) {
      this.toggleContentCollect(!nextState)
      return { isSuccess: false, type: nextState ? 'collect' : 'uncollect' }
    }

    return { isSuccess: true, type: nextState ? 'collect' : 'uncollect' }
  }

  // 通过作业id查询对应的日记id 并缓存下来
  async homeworkId2MomentId(homeworkId: number, sourceType = MomentSourceType.SunDrying) {
    const valueInCache = this.momentIdCache.get(homeworkId)

    if (valueInCache !== undefined) {
      console.log('命中homeworkId2MomentId缓存！')
      return valueInCache
    }

    const [_error, data] = await $service<string | null>({
      url: EnumApiHomework.GetMomentIdByHomeworkId,
      method: 'GET',
      data: { sourceType },
      params: { bizId: homeworkId, sourceType }
    })

    if (_error) {
      return null
    }

    this.momentIdCache.set(homeworkId, data)
    return data
  }

  // 通过作品id查询对应的日记id 并缓存下来
  async worksId2MomentId(worksId: number, sourceType = MomentSourceType.Show) {
    const valueInCache = this.worksMomentIdCache.get(worksId)

    if (valueInCache !== undefined) {
      console.log('命中worksId2MomentId缓存！')
      return valueInCache
    }

    const [_error, data] = await $service<string | null>({
      url: EnumApiHomework.GetMomentIdByHomeworkId,
      method: 'GET',
      data: { sourceType },
      params: { bizId: worksId, sourceType }
    })

    if (_error) {
      return null
    }

    this.worksMomentIdCache.set(worksId, data)
    return data
  }

  /**
   * 增加视频内容的播放量
   */
  public async onAddVideoPlayCount(momentId: string) {
    await $service({
      method: 'PUT',
      url: EnumApiHomework.VideoCount,
      data: { id: momentId }
    })
  }

  @action
  private updateContentVisibleRange(isPublic: boolean) {
    if (!this.content) return

    this.content = {
      ...this.content,
      isPublic
    }
  }

  @action
  private updateContentShareImage(url: string) {
    this.shareImage = url
  }

  @action
  private updateLoadingShareImage(isLoading) {
    this.isLoadingShareImage = isLoading
  }

  @action
  private toggleContentLike(isLike: boolean) {
    if (this.content) {
      this.content = {
        ...this.content,
        isLike: isLike,
        likeCount: isLike ? this.content.likeCount + 1 : Math.max(0, this.content.likeCount - 1)
      }
    }
  }

  @action
  private toggleContentCollect(isCollect: boolean) {
    if (this.content) {
      this.content = {
        ...this.content,
        isCollect: isCollect,
        collectCount: isCollect ? this.content.collectCount + 1 : Math.max(0, this.content.collectCount - 1)
      }
    }
  }

  private parseHomeworkData(data: HomeworkDetail, moment: DairyDetil | null): Content {
    // 后台是否开启提交批阅功能，同时当前作业任务下是否有作业未批阅
    const reviewChooseEnableAndVisible = !!data.homeworkReviewChooseEnable && !data.homeworkReviewChooseVisible

    return {
      contentId: data.id,
      imageList: data.homeworkPicList,
      memberId: data.memberId,
      memberNickname: data.memberNickname,
      title: data.title,
      content: data.content,
      tag: data.homeworkTaskTitle || '',
      createTime: data.createTime,
      reviewed: data.reviewed,
      showReviewedflag: reviewChooseEnableAndVisible && data.reviewedFlag === 1,
      allowReviewed: reviewChooseEnableAndVisible && data.reviewedFlag !== 1,
      isPublic: data.visibleRange === EnumVisibleRange.Public,
      isViolation: data.isViolation,
      allowLikeOrCollect: data.isViolation === 1 ? false : moment ? true : false,
      allowShare: data.isViolation === 1 ? false : data.visibleRange === EnumVisibleRange.Privacy ? false : moment ? true : false,
      isLike: moment ? moment.like === true : false,
      isCollect: moment ? moment.collect === true : false,
      likeCount: moment ? (typeof moment.likeCount === 'number' ? moment.likeCount : 0) : 0,
      collectCount: moment ? (typeof moment.collectCount === 'number' ? moment.collectCount : 0) : 0,
      collectType: moment ? mapCollectType(moment.sourceType) : null,
      videoUrl: moment ? (typeof moment.videoUrl === 'string' ? moment.videoUrl : '') : '',
      videoCover: Array.isArray(moment?.pictures) && moment.pictures.length > 0 ? moment.pictures[0].waterUrl : ''
    } satisfies Content
  }

  private parseDairyData(data: DairyDetil): Content {
    return {
      contentId: data.id,
      imageList: data.pictures.map(i => {
        return {
          originalUrl: i.waterUrl,
          originalWidth: i.originWidth,
          originalHeight: i.originHeight,
          waterUrl: i.waterUrl
        }
      }),
      memberId: data.memberId,
      memberNickname: data.memberNickname,
      title: data.title,
      content: data.content,
      tag: data.sourceDescription || '',
      createTime: data.createTime,
      showReviewedflag: false,
      allowReviewed: false,
      isPublic: data.visibleRange === EnumVisibleRange.Public,
      isViolation: data.isViolation,
      allowLikeOrCollect: data.isViolation !== 1,
      allowShare: data.isViolation !== 1 && data.visibleRange === EnumVisibleRange.Public,
      isLike: data.like === true,
      isCollect: data.collect === true,
      likeCount: typeof data.likeCount === 'number' ? data.likeCount : 0,
      collectCount: typeof data.collectCount === 'number' ? data.collectCount : 0,
      collectType: mapCollectType(data.sourceType),
      videoUrl: typeof data.videoUrl === 'string' ? data.videoUrl : '',
      videoCover: Array.isArray(data.pictures) && data.pictures.length > 0 ? data.pictures[0].waterUrl : ''
    } satisfies Content
  }

  // 渲染mock数据
  public loadMockContent(data) {
    // 关闭loading
    if (this.loading) {
      this.updateLoading(false)
    }
    // 加载日记数据
    this.updateContent(data.homework)
    // 加载评论数据
    this.UserComments.loadMockComment(data.comment)
  }

  public async loadContent(option: Omit<HomeworkOption, 'momentId'> | Omit<DairyOption, 'homeworkId'>, currentUserId: string) {
    if (!this.loading) {
      this.updateLoading(true)
    }

    try {
      this.fullOptions = option

      if (option.type === 'homework') {
        const [error, homeworkInfo] = await $service<HomeworkDetail>({
          url: EnumApiHomework.HomeworkDetail,
          method: 'GET',
          data: { id: option.homeworkId },
          params: { id: option.homeworkId }
        })

        if (error) {
          if (error.errCode == 1081100001) {
            // 作业被删除
            this.updateContentError(EnumConentError.Removed)
          }
          return
        }

        // 反查一下日记id
        /**
         * 1直播 3
         * 2展览 1
         */
        const momentId = await this.homeworkId2MomentId(option.homeworkId, homeworkInfo.bizType === 2 ? 1 : 3)

        // 如果包含日记id 再去查询一下日记信息
        if (typeof momentId === 'string') {
          this.fullOptions.momentId = momentId

          const [, dairy] = await $service<DairyDetil>({
            url: EnumApiHomework.MomentDetail,
            method: 'GET',
            data: { id: momentId },
            params: { id: momentId }
          })

          // 增加日记访问量
          // await $service({
          //   url: EnumApiHomework.MomentView,
          //   method: 'PUT',
          //   data: { id: momentId }
          // })

          this.updateContent(this.parseHomeworkData(homeworkInfo, dairy))
        } else {
          this.updateContent(this.parseHomeworkData(homeworkInfo, null))
        }
      } else if (option.type === 'dairy') {
        const [error, dairyDetail] = await $service<DairyDetil>({
          url: EnumApiHomework.MomentDetail,
          method: 'GET',
          data: { id: option.momentId },
          params: { id: option.momentId }
        })

        // 增加日记访问量
        // await $service({
        //   url: EnumApiHomework.MomentView,
        //   method: 'PUT',
        //   data: { id: option.momentId }
        // })

        if (error) {
          if (error.errCode === 1051100005) {
            // 日记被私密
            this.updateContentError(EnumConentError.Private)
          } else if (error.errCode === 1051100024) {
            // 日记被删除
            this.updateContentError(EnumConentError.Removed)
          }
          return
        }

        // 必须是自己的才能设置作业id
        if (
          dairyDetail.sourceType === MomentSourceType.SunDrying &&
          typeof dairyDetail.bizId === 'number' &&
          dairyDetail.memberId === currentUserId
        ) {
          this.fullOptions.homeworkId = dairyDetail.bizId
        }

        this.updateContent(this.parseDairyData(dairyDetail))
      }
    } finally {
      this.updateLoading(false)

      console.warn(JSON.stringify(this.fullOptions))
    }
  }

  // 获取访问者对内容所有者的关注状态
  public loadFocusStatus = async (memberId: string) => {
    if (!memberId) return

    const [err, res] = await $service<EnumFollowStatus>({
      url: EnumApiUser.FocusStatus,
      params: { targetMemberId: memberId }
    })

    if (err) throw err

    this.setIsFollow(res === EnumFollowStatus.Yes)
  }

  public toggleFollow = async (memberId: string) => {
    // 更新关注状态
    this.setIsFollow(!this.isFollow)

    const [, res] = await $service<boolean>({
      url: EnumApiUser.FocusUser,
      method: 'POST',
      data: {
        focusStatus: this.isFollow ? EnumFollowStatus.Yes : EnumFollowStatus.No,
        followedId: memberId
      },
      params: {
        focusStatus: this.isFollow ? EnumFollowStatus.Yes : EnumFollowStatus.No,
        followedId: memberId
      }
    })

    if (res === true) return true

    // 回滚
    this.setIsFollow(!this.isFollow)

    return false
  }

  // 指定要批阅的作业
  async chooseReview(homeworkId: number) {
    const [, res] = await $service<boolean>({
      url: EnumApiHomework.ChooseReviewed,
      method: 'PUT',
      params: { homeworkId }
    })

    return res === true
  }
}
